<!-- The pad's dateline: what day this is, where the prices came from, what is
     left to spend, and the one thing the odds quietly assume.

     It used to be two blocks — a dateline in the page and a prices bar with a
     button under it — and between them they took a third of a phone screen
     before the first ticket. They say one thing about one day, so they are one
     line of small print now. The button went to the toolbar: "Update prices"
     is a page action, and a page action in the body of the page is a row of
     paper spent on a control that is used once a fortnight.

     THE TOOLBAR'S BUTTON IS A REFRESH NOW, not a second kind of fetch. It used
     to be "Update prices" — buy the sports board again — sitting beside a
     pull-to-refresh that re-read the same board for nothing: two gestures that
     looked alike and did different things. They are one action (`refreshToday`
     in store.js), and the button and the pull call it.

     AND IT BUYS THE PRICES. The first version of it re-read the day the
     server had already stored: a tap moved nothing and said "these are the
     newest prices", which is the app claiming a fetch it never made. A refresh
     is `updatePrices` now, which spends one credit a sport out of a shared 500
     a month, plus the racing card and its movers wherever racing is switched on
     at all. So the confirm prints what the tap costs BEFORE it is spent, in
     both halves and out of both keys, and the pull comes through the same
     gate.

     The credits, the arithmetic and the words stay here, with the line they are
     about; the toolbar only pulls the trigger. -->
<template>
  <p class="pad-line small-print">{{ line }}</p>

  <!-- The recipe only lets a destructive action stop and ask, and a spent
       credit is the one thing here that cannot be undone. Same idiom as the
       delete confirm on Strategies. -->
  <IonAlert
    :is-open="asking"
    :header="header"
    :message="message"
    :buttons="buttons"
    @didDismiss="asking = false"
  />
</template>

<script setup>
import { computed, ref } from 'vue';
import { IonAlert } from '@ionic/vue';
import { toast } from '../core/ui';
import { isDemo } from '../core/demo.js';
import {
  oddsCredits, oddsStatus, padDate, pricesFetchedAt, racingCredits, refreshCost, refreshToday,
  riskProfile, showingTomorrow,
} from '../store.js';
import { day, time } from '../lib/format.js';
import { bookmakerName } from '../lib/odds.js';

const asking = ref(false);
const busy = ref(false);

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** One key's meter: what is left, and the plan's size where it can be known. */
const meter = (c) => {
  const remaining = n(c?.remaining);
  const used = n(c?.used);
  // The plan's size is remaining + used, and only a key that reports both can
  // be printed as "of". One number alone is still worth saying.
  return { remaining, used, total: remaining === null || used === null ? null : remaining + used };
};

const credits = computed(() => meter(oddsCredits.value));

/**
 * The OTHER key. Racing is bought from PuntersEdge on a 1,500-credit month of
 * its own, and a Refresh now spends out of both at once — so a confirm that
 * named one balance would be counting a tap against a plan half of it does not
 * come out of.
 */
const racing = computed(() => meter(racingCredits.value));

/** "467 of 500", or "467" where the plan's size cannot be known, or null. */
const poolLeft = (m) => {
  if (m.remaining === null) return null;
  return m.total === null
    ? m.remaining.toLocaleString()
    : `${m.remaining.toLocaleString()} of ${m.total.toLocaleString()}`;
};

const creditsLeft = computed(() => {
  const left = poolLeft(credits.value);
  return left === null ? null : `${left} credits left`;
});

// The board's own states only — never the sign-in or no-key screens, which are
// about getting a board at all. `loading` counts once there IS one: a
// pull-to-refresh flips the status for a moment, and a line that lost half its
// clauses and got them back would shift the pad under the thumb that pulled it.
const priced = computed(() => oddsStatus.value === 'ready'
  || (oddsStatus.value === 'loading' && !!pricesFetchedAt.value));

/**
 * The whole line: the day, then everything known about the prices under it.
 *
 * The bookmaker is named here and nowhere else on Today. A compact ticket has
 * no footer, so this is the one place the page says whose prices these are.
 *
 * WHAT THE CHANCE ASSUMES IS NO LONGER SAID HERE. It used to be, because no
 * ticket on Today carried it; every ticket carries it now, on its own returns
 * line, where it stays attached to the number it qualifies — through a scroll,
 * a screenshot and a share. A dateline caveat is one a reader has scrolled past
 * by the time they read the figure it was about.
 */
const line = computed(() => {
  const date = day(padDate.value);
  if (isDemo.value) return `${date} · sample prices`;
  const at = priced.value && pricesFetchedAt.value ? pricesFetchedAt.value : null;
  return [
    date,
    showingTomorrow.value ? 'tomorrow’s board' : null,
    at ? `${bookmakerName(riskProfile.value.bookmaker)} prices ${time(at)}` : null,
    priced.value ? creditsLeft.value : null,
  ].filter(Boolean).join(' · ');
});

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * What the tap will spend, held while the alert is up so it cannot change under
 * it: `{ odds, racing, total }` as `refreshCost` answers.
 */
const cost = ref({ odds: 0, card: 0, movers: 0, racing: 0, total: 0 });

/**
 * The two halves, in the order they are bought, then what is left.
 *
 * Both halves are named because they come out of the same 500 and are bought
 * for different reasons: a punter who has never opened the Racing tab should
 * not read a number with a card in it, and one who has is owed the split.
 *
 * A HALF THAT COSTS NOTHING IS NOT NAMED. A board that cannot be bought again
 * still buys the racing card, and the sentence used to open "About 0 credits
 * for today's prices across 0 sports, and 5 for the racing card" — which is the
 * confirm counting a thing the tap will not do. The card alone reads "About 2
 * credits for today's racing card (5 with movers)."
 */
/**
 * Whose prices the tap is about. The pad rolls to tomorrow every evening and
 * that board is bought again like any other, so a confirm that said "today's"
 * there would be naming a day the tap will not fetch. The racing card below is
 * always today's: there is one card, and it is today's races.
 */
const whoseBoard = computed(() => (showingTomorrow.value ? 'tomorrow’s' : 'today’s'));

const header = computed(() => `Fetch ${whoseBoard.value} prices again?`);

/**
 * THE CARD'S OWN PRICE, AND WHAT THIS TAP ADDS TO IT: "2 for the racing card
 * (5 with movers)".
 *
 * Two credits is what the app spends on the card by itself, a few times a day,
 * following the board (`autoLoadRacing` in store.js). The other three are the
 * movers, and they ride a deliberate Refresh and nothing else — so the confirm
 * names the card's own price first and the total it is actually about second.
 * A tap that bought no movers says the one number.
 */
const withMovers = computed(() => (cost.value.movers ? ` (${cost.value.racing} with movers)` : ''));

/**
 * WHAT IS LEFT, IN AS MANY POOLS AS THE TAP SPENDS FROM.
 *
 * One half and the balance is unqualified, exactly as it always read. Both
 * halves and each number is named for the key it is on: The Odds API's 500 and
 * PuntersEdge's 1,500 are different plans on different months, and printing one
 * figure over a tap that spends from both would be arithmetic about nothing.
 */
const balance = computed(() => {
  const { odds, racing: racingCost } = cost.value;
  const sports = poolLeft(credits.value);
  const races = poolLeft(racing.value);
  if (odds && racingCost && sports && races) {
    return `${sports} sports credits and ${races} racing credits left this month.`;
  }
  if (racingCost && !odds && races) return `${races} racing credits left this month.`;
  return sports === null ? null : `${sports} left this month.`;
});

const message = computed(() => {
  const { odds, card, racing: racingCost } = cost.value;
  const pricesPart = `About ${plural(odds, 'credit')} for ${whoseBoard.value} prices across ${plural(odds, 'sport')}`;
  const head = odds
    ? (racingCost ? `${pricesPart}, and ${card} for the racing card${withMovers.value}.` : `${pricesPart}.`)
    : `About ${plural(card, 'credit')} for today’s racing card${withMovers.value}.`;
  return [head, balance.value].filter(Boolean).join(' ');
});

const buttons = computed(() => [
  { text: 'Not now', role: 'cancel' },
  { text: 'Refresh', handler: () => { run(); } },
]);

/**
 * WHAT HAPPENED TO THE PRICES — the first clause of the toast, and on its own
 * the whole of what the bar used to say.
 *
 * Every branch says something. The old bar's failure was the opposite — it had
 * one sentence for a fetch that never ran and it read like a success — and a
 * refusal the app can explain ("too soon", "that is tomorrow's board") is worth
 * more than a spinner that stops.
 */
function saidOdds(result) {
  if (result?.ok) {
    return ['Refreshed',
      pricesFetchedAt.value ? `prices from ${time(pricesFetchedAt.value)}` : null,
      n(result.cost) === null ? null : `used ${plural(result.cost, 'credit')}`,
    ].filter(Boolean).join(' · ');
  }
  switch (result?.reason) {
    case 'too-soon': return tooSoon(result.retryAt);
    // A date nothing will sell: past tomorrow in Sydney, which a phone an hour
    // or two ahead of Sydney can reach and nothing else can.
    //
    // IT NO LONGER FIRES ON THE EVENING ROLL, which is the whole of the 6:13 pm
    // bug. This said "Tomorrow's prices can't be refreshed yet" and said it
    // every evening, on the board the punter was actually betting into, because
    // the rolled pad was printing tomorrow and both sides refused tomorrow.
    // Tomorrow is refreshable now (`canRefreshBoard` in store.js, `refreshable`
    // in server/odds.mjs), so the toast is back to naming something rare.
    case 'not-today': return 'That board can’t be refreshed yet';
    // A break buys nothing, which includes the prices.
    case 'break': return 'You’re on a break. Nothing was fetched.';
    case 'demo': return 'Sample day. There is nothing to fetch.';
    case 'offline': return 'You’re offline. Nothing was fetched.';
    default: return 'Couldn’t reach the odds. Nothing was fetched.';
  }
}

/**
 * THE WHOLE TAP, IN ONE SENTENCE: the prices, the card, the money.
 *
 * A refresh is three independent halves (`refreshToday` in store.js) and the
 * toast is the only thing that tells the punter which of them happened. The bug
 * this composition exists for: a refused odds half used to be the entire toast
 * — "Tomorrow's prices can't be refreshed yet" — on a tap that had in fact gone
 * and bought a fresh racing card and settled two bets, so the app reported a
 * failure and hid the two things it had just done.
 *
 * A half that did nothing is not mentioned. There is no "0 bets settled": a
 * count of nothing is noise on a line somebody reads in two seconds.
 */
function said(result) {
  return [
    saidOdds(result),
    result?.racing ? 'racing card refreshed' : null,
    result?.settled > 0 ? `${plural(result.settled, 'bet')} settled` : null,
  ].filter(Boolean).join(' · ');
}

/** The floor, as a wait rather than a failure: when they were read, and when. */
function tooSoon(retryAt) {
  const at = pricesFetchedAt.value;
  const head = at ? `Prices were refreshed at ${time(at)}` : 'Prices were just refreshed';
  const when = Number.isFinite(Date.parse(retryAt ?? '')) ? time(retryAt) : null;
  return when ? `${head}. Try again after ${when}` : `${head}. Try again shortly`;
}

/** The refresh itself, whether it was asked about or not. */
async function run() {
  if (busy.value) return;
  busy.value = true;
  try {
    toast(said(await refreshToday()));
  } catch {
    toast('Couldn’t reach the odds. Nothing was fetched.');
  } finally {
    busy.value = false;
  }
}

/**
 * REFRESH. The toolbar button and the pull both come through here: it asks
 * first where the tap will actually spend credits, and otherwise just goes.
 *
 * The state, the credit arithmetic and the words all live in this component,
 * and the trigger is the only thing that had to move.
 */
function refresh() {
  if (busy.value) return Promise.resolve();
  cost.value = refreshCost();
  // A tap that spends stops and asks. A tap that cannot spend anything — the
  // demo, the sample board, a break, a pad showing tomorrow — goes straight
  // through and comes back with the reason it could not.
  if (cost.value.total > 0) { asking.value = true; return Promise.resolve(); }
  return run();
}

defineExpose({ refresh, busy });
</script>

<style scoped>
/* One line, at the very top of the pad, with the first section's own top
   margin doing the separating underneath it. */
.pad-line {
  margin: 0;
}
</style>
